import React from "react";

import { useTranslation } from "react-i18next";

import { MoreOptions } from "../../../../../../../../../../components/MoreOptionsComponent/MoreOptions";
import { InvoicesCharges } from "../../../../../../../../../../models/invoice.charges";
import { InvoicesShipment } from "../../../../../../../../../../models/invoices.shipment";
import { LoadingCost } from "../../../../../../../../../../models/loading.cost";
import { ColumnWithKey } from "../../../../../../../../../../types";
import { CronJobType } from "../../../../../../../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../../../../../../../util/Format";

export const useInvoiceColumns = () => {
  const { t: translate } = useTranslation();

  const shipmentCostsColumns: ColumnWithKey<InvoicesCharges>[] = [
    {
      name: "invoiceNumber",
      title: `${translate("invoiceNumber")} #`,
    },
    {
      name: "description",
      title: `${translate("description")}`,
    },
    {
      name: "invoiceShipment",
      title: `${translate("order")} #`,
      formatComponent: (value?: InvoicesShipment) => {
        return (
          <MoreOptions
            options={value?.poNumber?.split(",") || []}
            title={translate("orders")}
            manyOptionsToShow={1}
          />
        );
      },
      searchIdentifier: "container_id",
    },
    {
      name: "invoiceShipment",
      title: `${translate("supplierName")}`,
      formatComponent: (value?: InvoicesShipment) => value?.supplierName,
    },
    {
      name: "invoiceShipment",
      title: `${translate("origin")}`,
      formatComponent: (value?: InvoicesShipment) => value?.origin,
    },
    {
      name: "invoiceShipment",
      title: `${translate("destination")}`,
      formatComponent: (value?: InvoicesShipment) => value?.destination,
    },
    {
      name: "invoiceShipment",
      title: `${translate("mode")}`,
      formatComponent: (value?: InvoicesShipment) =>
        value?.mode ? translate(value.mode.toLowerCase()) : "",
    },
    {
      name: "invoiceShipment",
      title: `${translate("eta")}`,
      formatText: (value?: InvoicesShipment) =>
        value?.eta ? formatDateWithoutTimeZone(String(value?.eta)) : "",
    },
  ];

  const LCRColumns: ColumnWithKey<LoadingCost>[] = [
    {
      name: "houseBill",
      title: translate("houseBill"),
    },
    {
      name: "shipment",
      title: translate("jobNumber"),
    },
    {
      name: "poNumber",
      title: translate("poNumber"),
    },
    {
      name: "supplierName",
      title: translate("supplierName"),
    },
    {
      name: "consol",
      title: translate("consol"),
    },
    {
      name: "mode",
      title: translate("mode"),
      formatComponent: (value: string) =>
        value ? translate(value.toLowerCase()) : "",
    },
    {
      name: "origin",
      title: translate("origin"),
    },
    {
      name: "destination",
      title: translate("destination"),
    },
    {
      name: "destinationCountry",
      title: translate("country"),
    },
    {
      name: "eta",
      title: translate("etaMonth"),
      formatText: (value?: string) => formatDateWithoutTimeZone(String(value)),
    },
    {
      name: "commercialInvoiceNumber",
      title: translate("commercialInvoiceNumber"),
    },
    {
      name: "commercialInvoiceDate",
      title: translate("commercialInvoiceDate"),
      formatText: (value?: string) => formatDateWithoutTimeZone(String(value)),
    },
    {
      name: "partNumber",
      title: translate("partNumber"),
    },
    {
      name: "brand",
      title: translate("brand"),
    },
    {
      name: "aud",
      title: translate("currency"),
    },
    {
      name: "receivedLinePrice",
      title: translate("receivedLinePrice"),
    },
    {
      title: translate("invoiceExchangeRate"),
      name: "exchangeRate",
    },
    {
      title: translate("audValue"),
      name: "audValue",
    },
    {
      title: translate("invoiceQuantity"),
      name: "invoiceQuantity",
    },
    {
      title: translate("lineDutyAmount"),
      name: "lineDutyAmount",
    },
    {
      title: translate("lineDutyPercent"),
      name: "lineDutyPercent",
    },
    {
      title: translate("nonAUEntryFees"),
      name: "entryFees",
    },
    {
      title: translate("originCharges"),
      name: "originCharges",
    },
    {
      title: translate("internationalFreight"),
      name: "internationalFreight",
    },
    {
      title: translate("destinationCharges"),
      name: "destinationCharges",
    },
    {
      title: translate("totalCosts"),
      name: "totalCosts",
    },
    {
      title: translate("totalUnitCosts"),
      name: "totalUnitCosts",
    },
    {
      title: translate("freightAUD"),
      name: "freightAud",
    },
    {
      title: `${translate("unitFob")} %`,
      name: "unitFob",
    },
    {
      title: translate("freightCPU"),
      name: "freightCpu",
    },
    {
      title: `${translate("freightFob")} %`,
      name: "freightFob",
    },
  ];

  const getInvoiceReportColumns = (type: CronJobType) => {
    switch (type) {
      case CronJobType.SHIPMENT_COST_REPORT:
        return shipmentCostsColumns;
      case CronJobType.LCR_REPORT:
        return LCRColumns;
      default:
        return LCRColumns;
    }
  };

  const getInvoiceReportRequiredFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.REPORT:
        return [];
      case CronJobType.CROSS_DOCK_STATUS:
        return [
          {
            name: "date",
            message: translate("selectAStartDate"),
          },
        ];
      default:
        return [];
    }
  };

  return { getInvoiceReportColumns, getInvoiceReportRequiredFilters };
};
